export const labs = [
  {
    id: "dns-server",
    title: "Build Your Own DNS Server",
    category: "linux",
    difficulty: "medium",
    time: "4h",
    tags: ["linux", "networking", "dns"],
    description: "You'll write a DNS server that actually resolves domains. By the end you'll never google 'how DNS works' again.",
    prerequisites: [
      "Basic understanding of TCP/IP networking",
      "Familiarity with UDP sockets",
      "Any programming language (Go, Python, or Node.js recommended)",
      "Linux environment with dig or nslookup available",
    ],
    objectives: [
      "Parse and construct DNS message headers (ID, flags, counts)",
      "Implement DNS question and answer section parsing",
      "Handle A, AAAA, CNAME, and MX record types",
      "Implement recursive resolution by querying root nameservers",
      "Add response caching with TTL-based expiration",
      "Verify your server responds correctly to dig queries",
    ],
    hints: [
      { title: "DNS Message Format", body: "DNS messages have a fixed 12-byte header followed by variable-length sections. The header contains the transaction ID (2 bytes), flags (2 bytes), and four 16-bit counts. Start by implementing a struct to parse this header." },
      { title: "Domain Name Encoding", body: "Domain names are encoded as a sequence of labels, each preceded by its length as a single byte. 'example.com' becomes [7]example[3]com[0]. Watch out for DNS name compression (pointer labels starting with 0xC0)." },
      { title: "Recursive Resolution", body: "Start with root nameservers (198.41.0.4). Query for the domain, follow the referral chain: root → TLD → authoritative → answer." },
    ],
    resources: [
      { title: "RFC 1035 — Domain Names Implementation", url: "https://www.ietf.org/rfc/rfc1035.txt" },
      { title: "How DNS Works (comic)", url: "https://howdns.works/" },
      { title: "DNS Message Format Explained", url: "https://www.cloudflare.com/learning/dns/dns-message-format/" },
    ],
    starterRepo: "https://github.com/fabiopalumbo/dioscuro-labs/tree/main/labs/01-dns-server",
  },
  {
    id: "reverse-proxy",
    title: "Build Your Own Reverse Proxy",
    category: "linux",
    difficulty: "medium",
    time: "4h",
    tags: ["linux", "networking", "proxy"],
    description: "Route traffic, handle load balancing, terminate TLS. The kind of thing you'd put in front of your app servers.",
    prerequisites: [
      "Basic HTTP protocol knowledge",
      "Familiarity with TCP sockets",
      "Understanding of TLS/SSL concepts",
    ],
    objectives: [
      "Accept incoming HTTP connections and forward to upstream servers",
      "Implement round-robin load balancing across multiple backends",
      "Add health checks to detect and skip unhealthy backends",
      "Handle HTTP headers correctly (Host, X-Forwarded-For, etc.)",
      "Add basic access logging",
    ],
    hints: [
      { title: "Start simple", body: "Begin with a single upstream. Get forwarding working before adding load balancing. The simplest proxy is just: accept connection, read request, open connection to upstream, forward request, forward response." },
      { title: "Header handling", body: "You need to set X-Forwarded-For to the client IP, and X-Forwarded-Proto to indicate the original protocol. Some upstreams need the original Host header preserved." },
    ],
    resources: [
      { title: "How Nginx handles a request", url: "https://nginx.org/en/docs/http/request_processing.html" },
      { title: "HTTP/1.1 RFC 7230", url: "https://www.rfc-editor.org/rfc/rfc7230" },
    ],
    starterRepo: "https://github.com/fabiopalumbo/dioscuro-labs/tree/main/labs/02-reverse-proxy",
  },
  {
    id: "container-runtime",
    title: "Build Your Own Container Runtime",
    category: "containers",
    difficulty: "hard",
    tier: "advanced",
    time: "8h",
    tags: ["containers", "namespaces", "cgroups"],
    description: "Build a container from scratch with namespaces and cgroups. After this, Docker stops being magic and starts being obvious.",
    prerequisites: [
      "Linux system administration",
      "Basic understanding of processes and system calls",
      "Go or C programming experience helpful",
      "Root access to a Linux system (VM recommended)",
    ],
    objectives: [
      "Create isolated process namespaces (PID, NET, MNT, UTS)",
      "Set up a root filesystem using chroot or pivot_root",
      "Implement cgroups for CPU and memory limits",
      "Add basic networking with a veth pair",
      "Run a process inside your container that can't see the host",
    ],
    hints: [
      { title: "Namespace order matters", body: "Create namespaces in this order: UTS (hostname), PID (process isolation), MNT (mount), NET (networking). Each gives you a different kind of isolation." },
      { title: "Use Alpine as rootfs", body: "Download an Alpine Linux rootfs tarball and extract it. It's tiny (~3MB) and has a working package manager. Perfect for testing your container." },
    ],
    resources: [
      { title: "Linux namespaces man page", url: "https://man7.org/linux/man-pages/man7/namespaces.7.html" },
      { title: "Containers from scratch (Liz Rice)", url: "https://www.youtube.com/watch?v=8fi7uSYlOdc" },
    ],
    starterRepo: "https://github.com/fabiopalumbo/dioscuro-labs/tree/main/labs/03-container-runtime",
  },
  {
    id: "docker-optimize",
    title: "Optimize a Docker Image",
    category: "containers",
    difficulty: "easy",
    time: "1h",
    tags: ["docker", "optimization"],
    description: "Your image is 1.2GB. Get it under 50MB. You'll learn multi-stage builds and wonder why you ever did it the other way.",
    prerequisites: [
      "Docker installed and running",
      "Basic Dockerfile knowledge",
    ],
    objectives: [
      "Analyze the existing 1.2GB image to find what's bloating it",
      "Implement a multi-stage build to separate build and runtime",
      "Choose an appropriate base image (Alpine, distroless, scratch)",
      "Get the final image under 50MB while keeping it functional",
    ],
    hints: [
      { title: "Use dive", body: "Install 'dive' to explore your image layer by layer. You'll see exactly which files are taking up space. Usually it's build tools, caches, and package manager artifacts." },
    ],
    resources: [
      { title: "Docker multi-stage builds", url: "https://docs.docker.com/build/building/multi-stage/" },
      { title: "Dive — explore Docker images", url: "https://github.com/wagoodman/dive" },
    ],
    starterRepo: "https://github.com/fabiopalumbo/dioscuro-labs/tree/main/labs/04-docker-optimize",
  },
  {
    id: "k8s-multi-service",
    title: "Deploy a Multi-Service App on K8s",
    category: "kubernetes",
    difficulty: "medium",
    time: "2h",
    tags: ["kubernetes", "deployment", "ingress"],
    description: "Deploy a real app on Kubernetes — frontend, backend, database. The kind of setup you'll actually use at work.",
    prerequisites: [
      "kubectl installed and configured",
      "Access to a Kubernetes cluster (minikube, kind, or cloud)",
      "Basic YAML knowledge",
    ],
    objectives: [
      "Create Deployments for frontend, backend, and database",
      "Configure Services for internal communication between components",
      "Set up ConfigMaps for environment-specific configuration",
      "Create an Ingress resource for external access",
      "Verify the full application works end-to-end",
    ],
    hints: [
      { title: "Start with the database", body: "Deploy the database first — it has no dependencies. Use a StatefulSet with a PVC if you want data persistence, or a simple Deployment for practice." },
      { title: "Use ClusterIP services", body: "Internal services (backend talking to DB) should use ClusterIP. Only the Ingress needs to be externally accessible." },
    ],
    resources: [
      { title: "Kubernetes Deployments", url: "https://kubernetes.io/docs/concepts/workloads/controllers/deployment/" },
      { title: "Kubernetes Services", url: "https://kubernetes.io/docs/concepts/services-networking/service/" },
    ],
    starterRepo: "https://github.com/fabiopalumbo/dioscuro-labs/tree/main/labs/05-k8s-multi-service",
  },
  {
    id: "helm-chart",
    title: "Create a Helm Chart from Scratch",
    category: "kubernetes",
    difficulty: "medium",
    time: "2h",
    tags: ["kubernetes", "helm"],
    description: "Write a Helm chart from scratch. Not copy-paste from Stack Overflow — actually understand what the templates do.",
    prerequisites: [
      "Helm 3 installed",
      "Basic Kubernetes knowledge (Deployments, Services)",
      "Understanding of Go template syntax helpful",
    ],
    objectives: [
      "Scaffold a new chart with helm create and understand each file",
      "Write templates for Deployment, Service, and Ingress",
      "Use values.yaml for configurable parameters",
      "Create a _helpers.tpl with reusable template definitions",
      "Test your chart with helm template and helm lint",
      "Deploy and upgrade your chart on a cluster",
    ],
    hints: [
      { title: "Delete the defaults first", body: "helm create generates a lot of boilerplate. Delete everything in templates/ and start from scratch. You'll learn more by writing each template yourself." },
    ],
    resources: [
      { title: "Helm chart template guide", url: "https://helm.sh/docs/chart_template_guide/" },
      { title: "Go template syntax", url: "https://pkg.go.dev/text/template" },
    ],
    starterRepo: "https://github.com/fabiopalumbo/dioscuro-labs/tree/main/labs/06-helm-chart",
  },
  {
    id: "github-actions-ci",
    title: "Build a GitHub Actions CI Pipeline",
    category: "cicd",
    difficulty: "easy",
    time: "1h",
    tags: ["ci/cd", "github-actions"],
    description: "Set up a CI pipeline that runs on every push. Lint, test, build, publish. The whole thing, in about an hour.",
    prerequisites: [
      "A GitHub repository with some code",
      "Basic YAML knowledge",
    ],
    objectives: [
      "Create a workflow file that triggers on push and pull requests",
      "Add a lint step using your project's linter",
      "Add a test step that runs your test suite",
      "Add a build step that produces an artifact",
      "Upload the build artifact for later use",
    ],
    hints: [
      { title: "Start with one job", body: "Get a single job working first (just echo 'hello'). Then add steps one at a time. Don't try to write the entire pipeline at once." },
    ],
    resources: [
      { title: "GitHub Actions quickstart", url: "https://docs.github.com/en/actions/quickstart" },
      { title: "Workflow syntax reference", url: "https://docs.github.com/en/actions/reference/workflow-syntax-for-github-actions" },
    ],
    starterRepo: "https://github.com/fabiopalumbo/dioscuro-labs/tree/main/labs/07-github-actions-ci",
  },
  {
    id: "argocd-gitops",
    title: "Implement GitOps with ArgoCD",
    category: "cicd",
    difficulty: "medium",
    tier: "advanced",
    time: "4h",
    tags: ["ci/cd", "argocd", "gitops"],
    description: "Make your git repo the source of truth for what's deployed. Push to main, ArgoCD handles the rest.",
    prerequisites: [
      "A running Kubernetes cluster",
      "kubectl configured",
      "A GitHub repo with Kubernetes manifests or Helm charts",
    ],
    objectives: [
      "Install ArgoCD on your cluster",
      "Create an Application resource pointing to your Git repo",
      "Enable auto-sync so pushes trigger deployments",
      "Configure health checks and sync policies",
      "Test a rollback by reverting a Git commit",
    ],
    hints: [
      { title: "Use the CLI first", body: "argocd app create is easier than writing YAML for your first app. Once it works, export the config to YAML with argocd app get --output yaml." },
      { title: "Sync policies", body: "Start with manual sync to understand the flow. Then add automated: { prune: true, selfHeal: true } when you're confident." },
    ],
    resources: [
      { title: "ArgoCD getting started", url: "https://argo-cd.readthedocs.io/en/stable/getting_started/" },
      { title: "GitOps principles", url: "https://opengitops.dev/" },
    ],
    starterRepo: "https://github.com/fabiopalumbo/dioscuro-labs/tree/main/labs/08-argocd-gitops",
  },
  {
    id: "terraform-vpc",
    title: "Write a Terraform Module for VPC",
    category: "iac",
    difficulty: "medium",
    tier: "advanced",
    time: "2h",
    tags: ["iac", "terraform", "aws"],
    description: "Write the VPC module you'll reuse in every project. Subnets, NAT, security groups — the foundation of everything.",
    prerequisites: [
      "Terraform installed (1.0+)",
      "AWS account with IAM credentials configured",
      "Basic understanding of AWS networking (VPC, subnets, routing)",
    ],
    objectives: [
      "Create a VPC with configurable CIDR block",
      "Add public and private subnets across multiple AZs",
      "Set up an Internet Gateway and NAT Gateway",
      "Configure route tables for public and private subnets",
      "Add security groups with sensible defaults",
      "Output VPC ID, subnet IDs, and security group IDs",
    ],
    hints: [
      { title: "Use variables for everything", body: "CIDR blocks, AZ count, naming prefixes — all should be variables. The goal is a module you can call with different values for dev/staging/prod." },
    ],
    resources: [
      { title: "Terraform AWS VPC module (official)", url: "https://registry.terraform.io/modules/terraform-aws-modules/vpc/aws/latest" },
      { title: "AWS VPC documentation", url: "https://docs.aws.amazon.com/vpc/latest/userguide/what-is-amazon-vpc.html" },
    ],
    starterRepo: "https://github.com/fabiopalumbo/dioscuro-labs/tree/main/labs/09-terraform-vpc",
  },
  {
    id: "prometheus-grafana",
    title: "Set Up Prometheus + Grafana",
    category: "observability",
    difficulty: "easy",
    time: "2h",
    tags: ["observability", "prometheus", "grafana"],
    description: "Set up monitoring so you can answer 'is it down?' before someone asks. Prometheus + Grafana, the standard combo.",
    prerequisites: [
      "Docker or Kubernetes access",
      "A service to monitor (any HTTP server will do)",
    ],
    objectives: [
      "Deploy Prometheus with a basic scrape config",
      "Configure Prometheus to scrape your target application",
      "Deploy Grafana and connect it to Prometheus as a data source",
      "Create a dashboard with request rate, error rate, and latency",
      "Set up an alert rule for high error rate",
    ],
    hints: [
      { title: "Docker Compose is fastest", body: "Use docker-compose with prometheus, grafana, and your app. Mount the prometheus.yml config file. You'll have monitoring running in 5 minutes." },
    ],
    resources: [
      { title: "Prometheus getting started", url: "https://prometheus.io/docs/prometheus/latest/getting_started/" },
      { title: "Grafana provisioning", url: "https://grafana.com/docs/grafana/latest/administration/provisioning/" },
    ],
    starterRepo: "https://github.com/fabiopalumbo/dioscuro-labs/tree/main/labs/10-prometheus-grafana",
  },
];
